/**
 * SERVICE: Agency Employee API
 * Servicio para endpoints específicos del rol Agency Employee
 */

import { ApiClient } from './api-client';

export interface AgencyOverview {
  agencyData: {
    name: string;
    id: number;
    code: string;
    address?: string;
    manager?: string;
    employees: number;
    status: string;
    group?: string;
  };
  agencyStatus: {
    color: string;
    icon: string;
    text: string;
  };
  currentShift: string;
  assignmentType: string;
  currentTime: string;
}

export interface AgencyIncident {
  id: number;
  title: string;
  description: string;
  priority: string;
  type: string;
  status: string;
  timestamp: string;
  reported_by?: string;
}

export interface AgencyInventoryItem {
  id: number;
  name: string;
  code: string;
  quantity: number;
  level: number;
  status: string;
  min_stock?: number;
  max_stock?: number;
}

export interface AgencyStats {
  incidents_count: number;
  employees_count: number;
  equipment_status: {
    operational: number;
    maintenance: number;
    offline: number;
  };
  recent_activities: any[];
}

class AgencyEmployeeService {
  private basePath = '/agency-employee';

  /**
   * Obtener vista general del empleado de agencia
   */
  async getOverview() {
    const response = await ApiClient.get<AgencyOverview>(`${this.basePath}/overview`);
    return response.data;
  }

  /**
   * Obtener KPIs principales
   */
  async getKPIs() {
    const response = await ApiClient.get(`${this.basePath}/kpis`);
    return response.data;
  }

  /**
   * Obtener KPIs operacionales
   */
  async getOperationalKPIs() {
    const response = await ApiClient.get(`${this.basePath}/operational-kpis`);
    return response.data;
  }

  /**
   * Obtener alertas activas
   */
  async getAlerts() {
    const response = await ApiClient.get(`${this.basePath}/alerts`);
    return response.data;
  }

  /**
   * Obtener estadísticas de operación
   */
  async getOperationStats() {
    const response = await ApiClient.get(`${this.basePath}/operation-stats`);
    return response.data;
  }

  /**
   * Obtener información de la agencia
   */
  async getAgencyInfo() {
    const response = await ApiClient.get(`${this.basePath}/agency-info`);
    return response.data;
  }

  /**
   * Obtener incidencias de la agencia
   */
  async getIncidents(filters?: { status?: string; priority?: string }) {
    const response = await ApiClient.get<AgencyIncident[]>(`${this.basePath}/incidents`, { params: filters });
    return response.data;
  }

  /**
   * Crear nueva incidencia.
   * P13-V1C: la clasificación canónica del intake es `classificationId` (obligatoria). El campo
   * `type` se retira del cable: hoy es un ALIAS de clasificación que el backend deniega con 400
   * INTAKE_BODY_CLASSIFICATION_ALIAS — el cuerpo del BFF se propaga íntegro al sink guardado.
   */
  async createIncident(data: {
    title: string;
    description: string;
    priority: string;
    classificationId: number;
  }) {
    const response = await ApiClient.post<AgencyIncident>(`${this.basePath}/incidents`, data);
    return response.data;
  }

  /**
   * Obtener equipamiento de la agencia
   */
  async getEquipment() {
    const response = await ApiClient.get(`${this.basePath}/equipment`);
    return response.data;
  }

  /**
   * Actualizar estado de equipo
   */
  async updateEquipmentStatus(equipmentId: number, status: string) {
    const response = await ApiClient.put(`${this.basePath}/equipment/${equipmentId}/status`, { status });
    return response.data;
  }

  /**
   * Obtener inventario local de la agencia
   */
  async getInventory() {
    const response = await ApiClient.get<AgencyInventoryItem[]>(`${this.basePath}/inventory`);
    return response.data;
  }

  /**
   * Solicitar inventario
   */
  async requestInventory(data: {
    item_code: string;
    quantity: number;
    reason: string;
  }) {
    const response = await ApiClient.post(`${this.basePath}/inventory/request`, data);
    return response.data;
  }

  /**
   * Obtener estadísticas generales
   */
  async getStats() {
    const response = await ApiClient.get<AgencyStats>(`${this.basePath}/stats`);
    return response.data;
  }

  /**
   * Obtener historial de actividades
   */
  async getActivitiesHistory(filters?: { limit?: number }) {
    const response = await ApiClient.get(`${this.basePath}/activities/history`, { params: filters });
    return response.data;
  }

  /**
   * Obtener notificaciones
   */
  async getNotifications() {
    const response = await ApiClient.get(`${this.basePath}/notifications`);
    return response.data;
  }

  /**
   * Enviar mensaje al supervisor
   */
  async sendMessageToSupervisor(data: {
    subject: string;
    message: string;
    priority: string;
  }) {
    const response = await ApiClient.post(`${this.basePath}/supervisor/message`, data);
    return response.data;
  }

  /**
   * Obtener estado de servicios
   */
  async getServicesStatus() {
    const response = await ApiClient.get(`${this.basePath}/services/status`);
    return response.data;
  }

  /**
   * Obtener estado de asistencia
   */
  async getAttendanceStatus() {
    const response = await ApiClient.get(`${this.basePath}/attendance/status`);
    return response.data;
  }

  /**
   * Obtener tickets locales
   */
  async getLocalTickets(filters?: { status?: string }) {
    const response = await ApiClient.get(`${this.basePath}/tickets/local`, { params: filters });
    return response.data;
  }
}

export default new AgencyEmployeeService();
